import {
  Button,
  Card,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconMinus, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, type CategoryNode, type Product } from "../lib/api";
import { cascadeLevels, hierarchyOf } from "../lib/hierarchy";
import { formatINR } from "../lib/money";
import {
  EmptyState,
  ErrorState,
  InlineError,
  LoadingSkeleton,
  NumField,
  RecordCard,
  SectionCard,
} from "./ui";

export interface OrderLine {
  productId: string;
  productName: string;
  sku: string;
  unitPrice: number;
  quantity: number;
}

/**
 * Product picker, SEARCH FIRST (REQ-ORD-1: pre-populated pickers, no
 * free-text product entry).
 *
 * Reversed on the customer's instruction after testing: Marketing staff know
 * the model number and type it. They do not think Company > Main category >
 * Category > Sub category and drill down to it - that is the catalogue's
 * filing order, not theirs, and making somebody choose four times to reach a
 * code they already knew is four chances to pick the wrong branch and
 * conclude the product is missing.
 *
 * So the model number is the input and **the hierarchy is derived from the
 * product**, shown read-only once one is chosen. Browsing the tree is still
 * here, behind a disclosure, because somebody who does NOT know the model
 * number still needs a way in.
 *
 * This is still not free-text product entry: the search only ever SELECTS a
 * catalogue row. Nothing typed here can create a product or a price.
 *
 * The running total is shown but never editable. Q7: prices are calculated
 * from the Store Manager's price list, and the only figure staff may enter is
 * a discount - which belongs on the billing screen, not here. The server
 * recalculates from the price history regardless, so this is a preview.
 */
export function ProductPicker({
  lines,
  onChange,
}: {
  lines: OrderLine[];
  onChange: (lines: OrderLine[]) => void;
}) {
  const [roots, setRoots] = useState<CategoryNode[]>([]);
  const [levelNames, setLevelNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  /** One selected node id per level, deepest last. Browse mode only. */
  const [path, setPath] = useState<string[]>([]);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>();
  const [searching, setSearching] = useState(false);
  /** The product chosen by search, which carries its own hierarchy. */
  const [found, setFound] = useState<Product>();
  const [browsing, setBrowsing] = useState(false);
  const searchSeq = useRef(0);

  useEffect(() => {
    void (async () => {
      try {
        const { categories, levels } = await api.catalogue();
        setRoots(categories);
        setLevelNames(levels);
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not load the catalogue.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * Searches on a short debounce, and DISCARDS a reply that is not the latest
   * request. Two keystrokes in flight can come back out of order, and the
   * list settling on the earlier one is the bug where the product you typed
   * is not in the results.
   */
  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setResults(undefined);
      setSearching(false);
      return;
    }
    const seq = (searchSeq.current += 1);
    setSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const { products: matches } = await api.products(needle);
          if (seq !== searchSeq.current) return;
          setResults(matches);
        } catch {
          if (seq === searchSeq.current) setResults([]);
        } finally {
          if (seq === searchSeq.current) setSearching(false);
        }
      })();
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * The options to show at each level: the roots, then the children of
   * whatever is selected above. Recomputed as one list so a change at any
   * level automatically truncates everything below it.
   */
  // Derived by the shared helper, which the battery-category picker also
  // uses: one cascade, so the two screens cannot offer different trees.
  const levels = useMemo(() => cascadeLevels(roots, path), [roots, path]);

  /** Products live on the deepest selected node, whatever depth that is. */
  const products = useMemo(() => {
    let node: CategoryNode | undefined;
    let options = roots;
    for (const id of path) {
      node = options.find((candidate) => candidate.id === id);
      if (!node) return [];
      options = node.children;
    }
    return node?.products ?? [];
  }, [roots, path]);

  // Whichever route found it. Search wins, because that is the primary flow.
  const selected: Product | undefined =
    found ?? products.find((product) => product.id === productId);
  const total = lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);

  /** Selecting at a level replaces it and clears every level below. */
  function selectAt(depth: number, id: string) {
    setPath((current) => {
      const next = current.slice(0, depth);
      if (id) next.push(id);
      return next;
    });
    setProductId("");
  }

  function addLine() {
    // An unpriced product cannot be added: there is no figure to record, and
    // the API refuses it. Better to say so than to bill zero.
    if (!selected || selected.dealerPrice === null || quantity < 1) return;

    const unitPrice = Number(selected.dealerPrice);
    const existing = lines.find((l) => l.productId === selected.id);
    // Same product twice would make the requested quantity ambiguous, and the
    // API rejects it - so combine instead.
    const next = existing
      ? lines.map((l) =>
          l.productId === selected.id ? { ...l, quantity: l.quantity + quantity } : l,
        )
      : [
          ...lines,
          {
            productId: selected.id,
            productName: selected.name,
            sku: selected.sku,
            unitPrice,
            quantity,
          },
        ];
    onChange(next);
    setProductId("");
    setFound(undefined);
    setQuery("");
    setResults(undefined);
    setQuantity(1);
  }

  /**
   * What to call the dropdown at this depth.
   *
   * Read from the catalogue's own list of level labels, which the SERVER
   * states. It used to lower-case each node's levelName identifier instead,
   * which is how a rename of the constants would have silently renamed the
   * screen - and in the rename that actually happened, wrongly.
   */
  function labelFor(depth: number, options: CategoryNode[]): string {
    return (
      options[0]?.levelLabel ?? levelNames[depth] ?? `Level ${depth + 1}`
    );
  }

  // Deliberately NOT blocking on the catalogue tree.
  //
  // Searching needs nothing but the search endpoint, and the tree is only for
  // browsing and for the level labels. Returning "Loading catalogue…" here
  // made the primary input unavailable until a second request finished, which
  // on a field connection is the difference between a screen that feels
  // instant and one that feels broken.
  if (error && roots.length === 0 && !results) {
    return <ErrorState message={error} />;
  }

  return (
    <Stack gap="sm">
      <TextInput
        id="pp-search"
        label="Model number"
        type="search"
        inputMode="search"
        autoComplete="off"
        leftSection={<IconSearch size={16} stroke={1.8} />}
        value={query}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setFound(undefined);
        }}
        placeholder="Type the model number, e.g. AAM-FL-00040B20L"
        description="Search by model number or name. The company, main category, category and sub category fill in from whatever you pick."
      />

      {searching && <LoadingSkeleton variant="list" count={2} />}

      {!found && results && results.length === 0 && !searching && (
        <EmptyState
          title={`Nothing matches \u201C${query.trim()}\u201D`}
          description="Check the model number, or browse the catalogue below."
        />
      )}

      {!found &&
        results &&
        results.slice(0, 25).map((product) => {
          const unpriced = product.dealerPrice === null;
          return (
            <RecordCard
              key={product.id}
              primary={product.sku}
              secondary={product.name}
              meta={
                hierarchyOf(product)
                  .map((node) => node.name)
                  .join(" \u203A ") || "Category unknown"
              }
              // Still SHOWN when unpriced - the searcher needs to know the
              // product exists and is waiting for a price, rather than
              // concluding it is missing - but not selectable. An unpriced row
              // gets no onClick at all, so it is not a button and cannot be
              // tabbed to or activated by keyboard either.
              {...(unpriced
                ? {}
                : {
                    onClick: () => {
                      setFound(product);
                      setProductId("");
                      setQuantity(1);
                    },
                  })}
              action={
                <Text
                  fz="sm"
                  fw={unpriced ? 600 : 500}
                  c={unpriced ? "yellow.8" : "gray.7"}
                  className={unpriced ? undefined : "num"}
                  ta="right"
                >
                  {unpriced ? "No price set" : formatINR(product.dealerPrice)}
                </Text>
              }
            />
          );
        })}
      {!found && results && results.length > 25 && (
        <Text fz="sm" c="gray.6">
          {results.length - 25} more match. Type more of the model number to
          narrow it.
        </Text>
      )}

      {/*
        The hierarchy, AUTO-POPULATED and read-only. Shown rather than hidden
        because it is the confirmation that the right thing was found - the
        model numbers differ by a character or two, and the category is what
        tells a human at a glance that this is the 4W flooded battery and not
        the 2W one.
      */}
      {found && (
        <SectionCard
          title="Product"
          action={
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setFound(undefined);
                setQuery("");
                setResults(undefined);
              }}
            >
              Change
            </Button>
          }
        >
          <Stack gap="sm">
            <Stack gap={2}>
              <Text fz="lg" fw={600} c="gray.9" className="num">
                {found.sku}
              </Text>
              <Text fz="md" c="gray.7">
                {found.name}
              </Text>
            </Stack>
            <Card withBorder={false} bg="gray.0" radius="md" p="sm">
              <Stack gap={6} component="dl" m={0}>
                {hierarchyOf(found).map((node) => (
                  <Group key={node.level} justify="space-between" gap="sm" wrap="nowrap">
                    <Text component="dt" fz="sm" c="gray.6">
                      {levelNames[node.level - 1] ?? `Level ${node.level}`}
                    </Text>
                    <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} ta="right">
                      {node.name}
                    </Text>
                  </Group>
                ))}
                {found.hsnCode && (
                  <Group justify="space-between" gap="sm" wrap="nowrap">
                    <Text component="dt" fz="sm" c="gray.6">
                      HSN
                    </Text>
                    <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} className="num">
                      {found.hsnCode}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Card>
          </Stack>
        </SectionCard>
      )}

      {/* Browsing is the fallback for somebody who does not know the code. */}
      {!found && roots.length > 0 && (
        <Button
          variant="subtle"
          color="gray"
          aria-expanded={browsing}
          onClick={() => setBrowsing((open) => !open)}
        >
          {browsing ? "Hide the catalogue" : "Or browse the catalogue"}
        </Button>
      )}
      {!found && loading && <LoadingSkeleton variant="list" count={2} />}
      {!found && !loading && roots.length === 0 && (
        <EmptyState
          title="No products yet"
          description="The Store Manager loads the catalogue and price list."
        />
      )}

      {!found &&
        browsing &&
        levels.map((level, depth) => {
          const label = labelFor(depth, level.options);
          const id = `pp-level-${depth}`;
          return (
            <Select
              key={id}
              id={id}
              label={label}
              placeholder={`Select ${label.toLowerCase()}`}
              value={level.selectedId || null}
              onChange={(value) => selectAt(depth, value ?? "")}
              data={level.options.map((node) => ({ value: node.id, label: node.name }))}
              searchable
              clearable
            />
          );
        })}

      {!found && browsing && products.length > 0 && (
        <Select
          id="pp-product"
          label="Product"
          placeholder="Select a product"
          value={productId || null}
          onChange={(value) => setProductId(value ?? "")}
          data={products.map((product) => ({
            value: product.id,
            label:
              product.dealerPrice === null
                ? `${product.sku} \u2014 no price set`
                : `${product.sku} \u2014 ${formatINR(product.dealerPrice)}`,
            // Disabled rather than hidden: the Store Manager needs to see
            // that the product exists and is waiting for a price.
            disabled: product.dealerPrice === null,
          }))}
          searchable
        />
      )}

      {selected && selected.dealerPrice === null && (
        <InlineError>
          {selected.sku} has no price yet. Ask the Store Manager to add it to
          the price list before ordering it.
        </InlineError>
      )}

      {selected && selected.dealerPrice !== null && (
        <Stack gap="sm">
          <NumField
            id="pp-qty"
            label="Quantity"
            min={1}
            max={10000}
            value={quantity}
            onChange={(value) => setQuantity(Math.max(1, Number(value) || 1))}
          />
          {selected.priceEffectiveFrom && (
            <Text fz="sm" c="gray.6">
              <span className="num">{formatINR(selected.dealerPrice)}</span> each,
              in force since {selected.priceEffectiveFrom}
              {selected.mrp === null ? "" : ` \u00B7 MRP ${formatINR(selected.mrp)}`}
            </Text>
          )}
          <Button variant="default" size="md" fullWidth onClick={addLine}>
            Add {selected.sku}
          </Button>
        </Stack>
      )}

      {lines.length > 0 && (
        <SectionCard title="On this order">
          <Stack gap="sm">
            {lines.map((line) => (
              <Group
                key={line.productId}
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
                gap="sm"
              >
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Text fz="md" fw={600} c="gray.9">
                    {line.productName}
                  </Text>
                  <Text fz="sm" c="gray.6" className="num">
                    {formatINR(line.unitPrice)} each today ={" "}
                    {formatINR(line.quantity * line.unitPrice)}
                  </Text>
                </Stack>

                {/* Steppers, on the line itself.
                    
                    Changing a quantity used to mean removing the line and
                    adding it again with a different number - three taps and a
                    search to turn 2 into 3. These are 32px targets either side
                    of the figure, which is the one control that gets used at a
                    customer's counter while holding a phone in one hand. */}
                <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Button
                    variant="default"
                    px={0}
                    w={32}
                    h={32}
                    aria-label={`One fewer ${line.productName}`}
                    onClick={() =>
                      // At one, the only way down is off the order - so that
                      // is what it does, rather than refusing at a floor of 1
                      // and leaving Remove as the only way out.
                      line.quantity <= 1
                        ? onChange(lines.filter((l) => l.productId !== line.productId))
                        : onChange(
                            lines.map((l) =>
                              l.productId === line.productId
                                ? { ...l, quantity: l.quantity - 1 }
                                : l,
                            ),
                          )
                    }
                  >
                    {line.quantity <= 1 ? (
                      <IconTrash size={15} stroke={1.8} />
                    ) : (
                      <IconMinus size={16} stroke={2} />
                    )}
                  </Button>
                  <Text fz={16} fw={700} className="num" w={28} ta="center">
                    {line.quantity}
                  </Text>
                  <Button
                    variant="default"
                    px={0}
                    w={32}
                    h={32}
                    aria-label={`One more ${line.productName}`}
                    onClick={() =>
                      onChange(
                        lines.map((l) =>
                          l.productId === line.productId
                            ? { ...l, quantity: l.quantity + 1 }
                            : l,
                        ),
                      )
                    }
                  >
                    <IconPlus size={16} stroke={2} />
                  </Button>
                </Group>
              </Group>
            ))}
            <Group justify="space-between" pt="xs" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
              <Text fz="md" c="gray.7">
                {/* Units AND lines: three of one battery and one each of three
                    are the same line count and very different vans. */}
                {lines.reduce((n, l) => n + l.quantity, 0)} unit
                {lines.reduce((n, l) => n + l.quantity, 0) === 1 ? "" : "s"} on{" "}
                {lines.length} line{lines.length === 1 ? "" : "s"}
              </Text>
              <Text fz="lg" fw={700} c="gray.9" className="num">
                {formatINR(total)}
              </Text>
            </Group>
            <Text fz="sm" c="gray.6">
              Calculated from the Store Manager&apos;s price list. GST is added,
              and any discount applied, when the bill is raised.
            </Text>
          </Stack>
        </SectionCard>
      )}
    </Stack>
  );
}
